import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

const formatDetails = [
  {
    name: "Singles (stroke play)",
    short: "Singles (stroke play)",
    allow: "95%",
    icon: "ri-user-line",
    desc: "Standard individual scoring (medal or Stableford). Each player competes independently.",
    how: "Playing Handicap = Course Handicap × 95%",
    example: "CH 14 × 95% = 13.3 → PH 13",
    tips: ["Most club competitions use this format", "Always used for medal competitions", "Handicap Index is updated from these scores"],
  },
  {
    name: "Singles match play",
    short: "Singles match play",
    allow: "100%",
    icon: "ri-sword-line",
    desc: "Head-to-head match between two players, awarding strokes based on Playing Handicaps.",
    how: "Playing Handicap = Course Handicap × 100%",
    example: "CH 14 × 100% = PH 14",
    tips: ["Full CH allowance — no reduction", "Strokes given on hardest holes first (Stroke Index 1, 2, 3...)", "Perfect for club knockouts and friendly matches"],
  },
  {
    name: "Fourball betterball",
    short: "Fourball betterball",
    allow: "85%",
    icon: "ri-group-line",
    desc: "Two-player teams. Each player plays their own ball; the better net score counts.",
    how: "Playing Handicap = Course Handicap × 85%",
    example: "CH 14 × 85% = 11.9 → PH 12",
    tips: ["Better-ball format — best score of the pair counts", "Common in club competitions and society days", "Both scores should still be recorded for handicapping"],
  },
  {
    name: "Foursomes",
    short: "Foursomes",
    allow: "50% combined",
    icon: "ri-shuffle-line",
    desc: "Partners play alternate shots using the same ball, taking alternate tee shots.",
    how: "Playing Handicap = (Player A CH + Player B CH) × 50%",
    example: "(CH 14 + CH 10) × 50% = 24 × 50% = PH 12",
    tips: ["Alternate tee shots — one player tees off on odd holes, the other on even", "Often played in club competitions and mixed events", "Requires strategy and partnership understanding"],
  },
  {
    name: "Greensomes",
    short: "Greensomes",
    allow: "60% low / 40% high",
    icon: "ri-leaf-line",
    desc: "Both players drive, the team selects the best tee shot, then alternate shots are played.",
    how: "Playing Handicap = (Low CH × 60%) + (High CH × 40%)",
    example: "(CH 14 × 60%) + (CH 10 × 40%) = 8.4 + 4 = PH 12",
    tips: ["Both tee off, then choose the best drive", "More forgiving than foursomes for inconsistent drivers", "Popular in club social competitions"],
  },
  {
    name: "Pinehurst / Chapman",
    short: "Pinehurst / Chapman",
    allow: "60% low / 40% high",
    icon: "ri-exchange-line",
    desc: "Both partners tee off, then play their partner's ball for the second shot. The team selects the best ball after two shots and plays alternate shots to finish the hole.",
    how: "Playing Handicap = (Low CH × 60%) + (High CH × 40%)",
    example: "(CH 14 × 60%) + (CH 10 × 40%) = 8.4 + 4 = PH 12",
    tips: ["Both tee off, then switch to partner's ball for second shot", "Best ball after two shots is selected", "Popular in US and international events"],
  },
  {
    name: "Best 1–4 of 4",
    short: "Best 1–4 of 4",
    allow: "85% / 85% / 100% / 100%",
    icon: "ri-bar-chart-grouped-line",
    desc: "Each golfer plays their own ball; the team score uses the best 1, 2, 3 or all 4 scores depending on competition rules.",
    how: "Best 1 of 4: 85% each | Best 2 of 4: 85% each | Best 3 of 4: 100% each | Best 4 of 4: 100% each",
    example: "Best 2 of 4: CH 14 × 85% = 11.9 → PH 12",
    tips: ["Flexible format for different team sizes", "Common in pro-am and charity events", "Scoring method depends on competition rules"],
  },
  {
    name: "Texas Scramble",
    short: "Texas Scramble",
    allow: "25% / 20% / 15% / 10%",
    icon: "ri-shake-hands-line",
    desc: "All players tee off, the best ball is chosen, and all players play each shot from that position.",
    how: "4-player teams: 25% + 20% + 15% + 10% (lowest to highest) | 3-player teams: 30% + 20% + 10%",
    example: "4 players: CHs 5, 12, 18, 24 → (5×25%) + (12×20%) + (18×15%) + (24×10%) = PH 9",
    tips: ["Very popular in charity and corporate events", "Team strategy matters — play the percentages", "Some clubs cap the maximum team handicap"],
  },
  {
    name: "Total Score of 2 – Match Play",
    short: "Total Score of 2 – Match Play",
    allow: "100%",
    icon: "ri-team-line",
    desc: "Both players in a team complete each hole. Their combined net score is compared against the other team to determine the match play result for the hole.",
    how: "Playing Handicap = Course Handicap × 100% for each player",
    example: "Player A CH 14 × 100% = PH 14, Player B CH 10 × 100% = PH 10",
    tips: ["Both players complete every hole", "Combined net score determines the hole", "Used in team match play competitions"],
  },
  {
    name: "Best 1–4 of 4 (Par/Bogey)",
    short: "Best 1–4 of 4 (Par/Bogey)",
    allow: "85% / 85% / 100% / 100%",
    icon: "ri-numbers-line",
    desc: "Similar to Stableford/medal versions, but each hole is scored as +1, 0 or −1 versus par/bogey using the best 1, 2, 3 or 4 net scores.",
    how: "Same allowances as Best 1–4 of 4: Best 1/2: 85% each | Best 3/4: 100% each",
    example: "Best 1 of 4: CH 14 × 85% = 11.9 → PH 12",
    tips: ["Scoring is +1/0/−1 per hole", "Similar to Stableford but with different scoring", "Follows standard team percentages"],
  },
  {
    name: "Custom formats",
    short: "Custom formats",
    allow: "Contact us",
    icon: "ri-settings-3-line",
    desc: "HANDICALC® can support society-specific or non-standard formats with bespoke allowances.",
    how: "Bespoke allowances based on competition rules",
    example: "Contact us to discuss your custom format",
    tips: ["Society-specific rules can be accommodated", "Non-standard formats welcome", "Contact us to discuss your needs"],
  },
];

const quickReference = [
  { format: "Singles (stroke play)", allow: "95%", match: false },
  { format: "Singles match play", allow: "100%", match: true },
  { format: "Fourball betterball", allow: "85%", match: false },
  { format: "Foursomes", allow: "50% combined", match: false },
  { format: "Greensomes", allow: "60% / 40%", match: false },
  { format: "Pinehurst / Chapman", allow: "60% / 40%", match: false },
  { format: "Best 1–4 of 4", allow: "85% / 85% / 100% / 100%", match: false },
  { format: "Texas Scramble", allow: "25/20/15/10%", match: false },
  { format: "Total Score of 2 – Match Play", allow: "100%", match: true },
  { format: "Best 1–4 of 4 (Par/Bogey)", allow: "85% / 85% / 100% / 100%", match: false },
  { format: "Custom formats", allow: "Contact us", match: false },
];

export default function FormatsPage() {
  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary-900">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950 via-primary-900 to-primary-800"></div>
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20golf%20course%20overhead%20view%20with%20golf%20flags%20and%20bunkers%20in%20warm%20golden%20light%2C%20soft%20focus%20atmospheric%20painterly%20style%2C%20emerald%20green%20and%20warm%20sandy%20gold%20colour%20palette%2C%20editorial%20artistic%20abstract%20golf%20landscape%20suitable%20as%20dark%20background%20with%20text%20contrast&width=1600&height=900&seq=formats-hero-bg&orientation=landscape')] bg-cover bg-center opacity-20"></div>
          <div className="relative mx-auto max-w-content px-4 md:px-6 pt-20 md:pt-28 pb-16 md:pb-24">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-xs font-medium text-accent-300">
              <i className="ri-file-list-line"></i>
              Complete reference
            </span>
            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">
              Supported formats &amp; shot allowances
            </h1>
            <p className="mt-5 text-primary-100 text-base md:text-lg max-w-2xl leading-relaxed">
              HANDICALC® supports a wide range of golf formats — from singles stroke play and match play to fourball betterball, foursomes, greensomes, Texas Scramble, Pinehurst and team events — and applies World Handicap System™ methodology allowance percentages suitable for each type of play. These allowances help ensure fairness across both social and competitive rounds for golfers, clubs and golf societies.
            </p>
            <p className="mt-3 text-primary-100 text-base md:text-lg max-w-2xl leading-relaxed">
              Allowances shown are typical and based on published World Handicap System™ methodology; competition Terms may vary. Always follow your club's Terms of Competition where they differ. If you need a format not listed here, contact us about custom configurations.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                Try the calculator <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                How it works
              </Link>
            </div>
          </div>
        </section>

        {/* Quick Reference Table */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">At a glance</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                Quick reference table
              </h2>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                All formats HANDICALC® supports, with their typical WHS™ allowance and whether the percentage applies to stroke play or match play.
              </p>
            </div>

            {/* Desktop table */}
            <div className="hidden md:block mt-10 md:mt-14 overflow-hidden rounded-xl border border-background-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-background-100">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold text-foreground-700">Format</th>
                      <th className="text-left px-6 py-3 font-semibold text-foreground-700">WHS™ Allowance</th>
                      <th className="text-left px-6 py-3 font-semibold text-foreground-700">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {quickReference.map((f, i) => (
                      <tr
                        key={f.format}
                        className={i % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}
                      >
                        <td className="px-6 py-3.5 font-medium text-foreground-950">{f.format}</td>
                        <td className="px-6 py-3.5">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold whitespace-nowrap">
                            {f.allow}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-foreground-600">
                          {f.match ? "Match play" : "Stroke play"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden mt-10 space-y-3">
              {quickReference.map((f, i) => (
                <div
                  key={f.format}
                  className={`rounded-xl border border-background-200 p-4 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground-950 text-sm leading-snug">{f.format}</p>
                      <p className="mt-1 text-xs text-foreground-500">{f.match ? "Match play" : "Stroke play"}</p>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold whitespace-nowrap shrink-0">
                      {f.allow}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Format Details */}
        <section className="bg-background-100 py-16 md:py-24 border-y border-background-200">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">Deep dive</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                How each format works
              </h2>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                Detailed explanations of every format, the logic behind the WHS™ allowance, and a worked example for each.
              </p>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {formatDetails.map((f) => (
                <article
                  key={f.name}
                  className="rounded-xl bg-background-50 border border-background-200 p-6 md:p-8"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-600 text-background-50">
                        <i className={`${f.icon} text-lg`} aria-hidden="true"></i>
                      </span>
                      <div>
                        <h3 className="font-heading font-bold text-foreground-950 text-lg md:text-xl">{f.name}</h3>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-bold whitespace-nowrap">
                      {f.allow}
                    </span>
                  </div>

                  <p className="mt-5 text-sm text-foreground-700 leading-relaxed">{f.desc}</p>

                  <div className="mt-5 rounded-lg bg-primary-50 border border-primary-200/50 px-4 py-3">
                    <p className="text-xs text-primary-800 font-semibold">Formula</p>
                    <p className="text-sm text-primary-900 mt-1 font-medium">{f.how}</p>
                  </div>

                  <div className="mt-3 rounded-lg bg-accent-100/70 border border-accent-200/50 px-4 py-3">
                    <p className="text-xs text-accent-900 font-semibold">Example</p>
                    <p className="text-sm text-accent-900 mt-1 font-medium">{f.example}</p>
                  </div>

                  <div className="mt-4 space-y-1.5">
                    {f.tips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Match Play vs Stroke Play */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">Key distinction</span>
                <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                  Match play vs stroke play
                </h2>
                <p className="mt-5 text-foreground-700 text-base leading-relaxed">
                  The same format can have different allowances depending on whether you are playing stroke play or match play. Stroke play rewards consistency over 18 holes; match play rewards winning individual holes. The WHS™ reflects this difference.
                </p>
                <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                  In match play, you receive your full Course Handicap (100%) because the hole-by-hole nature of the game means the advantage of having more strokes is less pronounced than in stroke play. In stroke play, the allowance is reduced (85%, 95%) because the cumulative score over 18 holes gives higher-handicap players a statistical advantage.
                </p>
              </div>
              <div className="space-y-4">
                <div className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8">
                  <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-4">Singles</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground-700">Stroke play</span>
                      <span className="font-semibold text-foreground-950 bg-secondary-100 rounded-full px-2.5 py-1 text-xs">95%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground-700">Match play</span>
                      <span className="font-semibold text-foreground-950 bg-secondary-100 rounded-full px-2.5 py-1 text-xs">100%</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl bg-primary-900 text-background-50 p-6 md:p-8">
                  <p className="text-sm text-primary-100 leading-relaxed">
                    <strong>Rule of thumb:</strong> Match play allowances are always higher than stroke play for the same format. In singles, the gap is 5 percentage points.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=Serene%20golf%20course%20fairway%20at%20dawn%20with%20warm%20golden%20light%20and%20misty%20pines%2C%20single%20golf%20flag%20in%20distance%2C%20painterly%20editorial%20style%2C%20deep%20emerald%20and%20warm%20gold%20colour%20palette%2C%20atmospheric%20cinematic%20composition%20suitable%20as%20dark%20background%20with%20text%20contrast&width=1600&height=800&seq=formats-cta-bg&orientation=landscape"
                alt="Golf course at dawn"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-900/75 to-primary-800/60"></div>
              <div className="relative z-10 px-6 md:px-14 py-14 md:py-20 text-center max-w-3xl mx-auto">
                <h2 className="font-heading font-bold text-background-50 text-3xl md:text-4xl leading-tight">
                  Pick your format, get your shots
                </h2>
                <p className="mt-4 text-background-100/90 text-base md:text-lg leading-relaxed">
                  HANDICALC® handles every format automatically. Just choose your course, tee and format — and get the correct WHS™ playing handicap instantly.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/calculator"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Open the calculator <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/faq"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm md:text-base font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Read the FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}